import { Card } from "primereact/card";
import { ApiResponse } from "../../services/base/apiResponse";
import { PlanSubjectResponse, StudentInfoAdminResponse } from "../../models/dtos";
import { Toolbar } from "primereact/toolbar";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Link } from "react-router-dom";
import { studentsApi } from "../../services/students/studentsApi";
import { format } from "date-fns";
import { useMemo, useState } from "react";
import { MultiSelect, MultiSelectChangeEvent } from "primereact/multiselect";
import { Message } from "primereact/message";
import CreateRecognitionFromStudentDialog from "./CreateRecognitionFromStudentDialog";

interface StudentDataProps {
  student?: ApiResponse<StudentInfoAdminResponse>;
}

type PlanSubjectWithGrade = PlanSubjectResponse & { maxGrade: number };

const gradeLookupKey = (subjectName: string | null | undefined, semester: number | null | undefined) =>
  `${(subjectName ?? "").trim()}|${semester ?? ""}`;

const renderPassingGrade = (rowData: PlanSubjectWithGrade) => {
  const maxGrade = rowData.maxGrade;
  if (maxGrade > 2) {
    return (
      <div className="flex align-items-center justify-content-center">
        <i className="pi pi-check text-green-500 mr-2" />
        <span className="text-green-500 font-bold">{maxGrade.toFixed(2)}</span>
      </div>
    );
  }
  const iconColor = rowData.subjectTypeName === "Свободно избираема" ? "text-yellow-500" : "text-red-500";
  return (
    <div className="flex align-items-center justify-content-center">
      <i className={`pi pi-times ${iconColor} mr-2`} />
      {maxGrade > 0 && <span className={`${iconColor} font-bold`}>{maxGrade.toFixed(2)}</span>}
    </div>
  );
};

const GradeData = ({ student }: StudentDataProps) => {
  const { data: gradesReport } = studentsApi.useGetStudentGradesReportQuery(student?.data?.studentId ?? 0, {
    skip: !student?.data?.studentId,
  });
  const [selectedSemesters, setSelectedSemesters] = useState<number[]>([]);
  const [lastCalculatedSemesters, setLastCalculatedSemesters] = useState<number[]>([]);
  const [showRecognitionDialog, setShowRecognitionDialog] = useState(false);
  const [
    triggerSelectedSemestersAverage,
    {
      data: selectedSemestersAverage,
      isFetching: isSelectedSemestersAverageLoading,
      error: selectedSemestersAverageError,
    },
  ] = studentsApi.useLazyGetStudentSelectedSemestersAverageQuery();

  const semesterOptions = useMemo(() => {
    if (!student?.data?.studentGrades?.length) {
      return [];
    }

    return Array.from(
      new Set(
        student.data.studentGrades
          .map((grade) => grade.protocolSemester)
          .filter((semester): semester is number => semester !== null),
      ),
    )
      .sort((a, b) => a - b)
      .map((semester) => ({
        label: `Семестър ${semester}`,
        value: semester,
      }));
  }, [student?.data?.studentGrades]);

  const planSubjectsWithGrades = useMemo((): PlanSubjectWithGrade[] => {
    const planSubjects = student?.data?.planSubjects ?? [];
    const studentGrades = student?.data?.studentGrades ?? [];
    const maxGradeByKey = new Map<string, number>();

    for (const grade of studentGrades) {
      const key = gradeLookupKey(grade.protocolSubjectName, grade.protocolSemester);
      const current = maxGradeByKey.get(key) ?? 0;
      const value = grade.grade || 0;
      if (value > current) {
        maxGradeByKey.set(key, value);
      }
    }

    return planSubjects.map((subject) => ({
      ...subject,
      maxGrade: maxGradeByKey.get(gradeLookupKey(subject.subjectName, subject.semester)) ?? 0,
    }));
  }, [student?.data?.planSubjects, student?.data?.studentGrades]);

  const handleCalculateSelectedSemestersAverage = () => {
    if (!student?.data?.studentId || selectedSemesters.length < 1) {
      return;
    }

    const sortedSemesters = [...selectedSemesters].sort((a, b) => a - b);
    setLastCalculatedSemesters(sortedSemesters);
    triggerSelectedSemestersAverage({
      studentId: student.data.studentId,
      semesters: sortedSemesters,
    });
  };

  const topButtons = (
    <>
      <Link to={`/plans/${student?.data.planId}`}>
        <Button label="Преглед на учебен план" icon="pi pi-eye" severity="info" />
      </Link>
      <Button
        label="Създай протокол за признаване"
        icon="pi pi-copy"
        severity="success"
        className="ml-2"
        disabled={!student?.data?.studentId}
        onClick={() => setShowRecognitionDialog(true)}
      />
    </>
  );

  const extraButtons = <>{/* <Button label="Експорт CSV" icon="pi pi-upload" severity="secondary" /> */}</>;

  const renderTablesForSemesters = () => {
    if (!student?.data?.studentGrades) {
      return null;
    }

    const sortedGrades = [...student.data.studentGrades]
      .sort((a, b) => {
        const semesterDiff = (a.protocolSemester ?? 0) - (b.protocolSemester ?? 0);
        if (semesterDiff !== 0) return semesterDiff;
        return (a.protocolSubjectName ?? "").localeCompare(b.protocolSubjectName ?? "");
      })
      .map((grade, index) => ({
        ...grade,
        rowKey: `${grade.protocolId ?? "none"}-${grade.protocolSubjectName ?? ""}-${grade.protocolSemester ?? ""}-${index}`,
      }));

    return (
      <Card title="Оценки">
        <DataTable
          value={sortedGrades}
          dataKey="rowKey"
          emptyMessage="Не бяха открити оценки за този студент."
        >
          <Column field="protocolSemester" header="Семестър" sortable filter></Column>
          <Column field="protocolSubjectName" header="Дисциплина" sortable filter></Column>
          <Column field="protocolTeacherName" header="Преподавател" sortable filter></Column>
          <Column
            field="protocolNumber"
            header="Номер протокол"
            sortable
            filter
            body={(rowData) => (
              <Link to={`/protocols/${rowData.protocolId}`}>
                <Button label={rowData.protocolNumber} severity="info" text />
              </Link>
            )}
          ></Column>
          <Column
            field="protocolIssueDate"
            header="Дата на издаване"
            sortable
            filter
            body={(rowData) =>
              rowData.protocolIssueDate ? format(new Date(rowData.protocolIssueDate), "dd.MM.yyyy") : ""
            }
          ></Column>
          <Column field="studentGradeTypeName" header="Тип оценка" sortable filter></Column>
          <Column
            field="grade"
            header="Оценка"
            sortable
            filter
            body={(rowData) => rowData.grade.toFixed(2)}
            bodyClassName={(rowData) => (rowData.grade <= 2 ? "text-red-500" : "")}
          ></Column>
        </DataTable>
      </Card>
    );
  };

  const renderAverages = () => {
    if (!gradesReport?.data) return null;

    return (
      <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Card>
          <div className="text-lg font-bold">Следване</div>
          <div className="text-2xl">{gradesReport.data.averageGrade?.toFixed(2) ?? "N/A"}</div>
        </Card>
        <Card>
          <div className="text-lg font-bold">Дипломиране</div>
          <div className="text-2xl">{gradesReport.data.graduationAverageGrade?.toFixed(2) ?? "N/A"}</div>
        </Card>
        <Card>
          <div className="text-lg font-bold">Общ среден успех</div>
          <div className="text-2xl">{gradesReport.data.totalAverageGrade?.toFixed(2) ?? "N/A"}</div>
        </Card>
        <Card>
          <div className="text-lg font-bold">Текущ семестър</div>
          <div className="text-2xl">{gradesReport.data.currentSemesterAverageGrade?.toFixed(2) ?? "N/A"}</div>
        </Card>
        <Card>
          <div className="text-lg font-bold">Предходен семестър</div>
          <div className="text-2xl">{gradesReport.data.previousSemesterAverageGrade?.toFixed(2) ?? "N/A"}</div>
        </Card>
        <Card>
          <div className="text-lg font-bold">Предходни два семестъра</div>
          <div className="text-2xl">{gradesReport.data.previousTwoSemestersAverageGrade?.toFixed(2) ?? "N/A"}</div>
        </Card>
        <Card>
          <div className="text-lg font-bold">Невзети изпити</div>
          <div className="text-2xl">{gradesReport.data.notTakenExamsCount ?? "N/A"}</div>
        </Card>
      </div>
    );
  };

  const renderSelectedSemestersAverage = () => {
    if (!student?.data?.studentId) {
      return null;
    }

    return (
      <Card title="Среден успех за избрани семестри" className="mb-4">
        <div className="p-fluid formgrid grid align-items-end">
          <div className="field col-12 md:col-8">
            <label htmlFor="selectedSemesters">Семестри</label>
            <MultiSelect
              id="selectedSemesters"
              value={selectedSemesters}
              options={semesterOptions}
              optionLabel="label"
              optionValue="value"
              onChange={(event: MultiSelectChangeEvent) => setSelectedSemesters(event.value ?? [])}
              placeholder="Изберете поне 1 семестър"
              display="chip"
              className="w-full"
            />
            {selectedSemesters.length < 1 && <small className="text-orange-500">Изберете минимум 1 семестър.</small>}
          </div>

          <div className="field col-12 md:col-4">
            <Button
              label="Изчисли"
              icon="pi pi-calculator"
              onClick={handleCalculateSelectedSemestersAverage}
              disabled={selectedSemesters.length < 1 || isSelectedSemestersAverageLoading}
              loading={isSelectedSemestersAverageLoading}
              className="w-full"
            />
          </div>
        </div>

        {selectedSemestersAverageError && (
          <Message
            severity="error"
            text="Възникна грешка при изчисляването на средния успех за избраните семестри."
            className="w-full mt-2"
          />
        )}

        {lastCalculatedSemesters.length >= 1 && (
          <div className="mt-3 p-3 border-1 border-round surface-border">
            <div className="text-sm text-600">Семестри: {lastCalculatedSemesters.join(", ")}</div>
            <div className="text-xl font-bold mt-2">
              Среден успех: {selectedSemestersAverage?.data?.averageGrade?.toFixed(2) ?? "N/A"}
            </div>
            <div className="text-sm text-600 mt-1">
              Брой оценки в изчислението: {selectedSemestersAverage?.data?.gradesCount ?? "N/A"}
            </div>
          </div>
        )}
      </Card>
    );
  };

  const renderPlanSubjects = () => {
    if (!planSubjectsWithGrades.length) {
      return null;
    }

    const semesters = Array.from(new Set(planSubjectsWithGrades.map((subject) => subject.semester))).sort(
      (a, b) => a - b,
    );

    return semesters.map((semester) => {
      const semesterSubjects = planSubjectsWithGrades.filter((subject) => subject.semester === semester);

      return (
        <Card key={semester} title={`Учебен план - Семестър ${semester}`} className="mt-4">
          <DataTable
            value={semesterSubjects}
            dataKey="planSubjectId"
            emptyMessage={`Не бяха открити дисциплини за семестър ${semester}.`}
          >
            <Column field="subjectName" header="Дисциплина" sortable filter></Column>
            <Column field="credits" header="Кредити" sortable filter style={{ width: "10%" }}></Column>
            <Column
              field="hours"
              header="Часове"
              style={{ width: "10%" }}
              body={(rowData) => (
                <div>
                  {rowData.lectureHours > 0 && `Л:${rowData.lectureHours} `}
                  {rowData.exerciseHours > 0 && `У:${rowData.exerciseHours} `}
                  {rowData.labHours > 0 && `Лаб:${rowData.labHours} `}
                  {rowData.practiceHours > 0 && `Пр:${rowData.practiceHours}`}
                </div>
              )}
            ></Column>
            <Column
              field="hoursWeekly"
              header="Седмични часове"
              style={{ width: "10%" }}
              body={(rowData) => (
                <div>
                  {rowData.lectureHoursWeekly > 0 && `Л:${rowData.lectureHoursWeekly} `}
                  {rowData.exerciseHoursWeekly > 0 && `У:${rowData.exerciseHoursWeekly} `}
                  {rowData.labHoursWeekly > 0 && `Лаб:${rowData.labHoursWeekly} `}
                  {rowData.practiceHoursWeekly > 0 && `Пр:${rowData.practiceHoursWeekly}`}
                </div>
              )}
            ></Column>
            <Column field="subjectTypeName" header="Тип" sortable filter style={{ width: "10%" }}></Column>
            <Column
              field="subjectControlTypeName"
              header="Форма на контрол"
              sortable
              filter
              style={{ width: "15%" }}
            ></Column>
            <Column
              field="maxGrade"
              header="Взет изпит"
              className="text-center"
              style={{ width: "8%" }}
              body={renderPassingGrade}
            ></Column>
          </DataTable>
        </Card>
      );
    });
  };

  return (
    <div>
      {renderAverages()}
      {renderSelectedSemestersAverage()}
      {renderTablesForSemesters()}
      <Toolbar start={topButtons} end={extraButtons} className="mt-4" />
      {renderPlanSubjects()}
      {student?.data && (
        <CreateRecognitionFromStudentDialog
          visible={showRecognitionDialog}
          onHide={() => setShowRecognitionDialog(false)}
          student={student.data}
        />
      )}
    </div>
  );
};

export default GradeData;
